export interface JobReady {
  key: string;
  payload: unknown;
}

export interface EventSourceLike {
  addEventListener: (type: string, cb: (e: MessageEvent) => void) => void;
  close: () => void;
}

interface TokenResult {
  token: string;
  ns: string;
}

interface ConnectedEvent {
  connId: string;
}

interface ResolverEntry {
  resolve: (r: JobReady) => void;
  immediateCheck: () => Promise<JobReady | null>;
}

interface Deps {
  eventSourceFactory: (url: string) => EventSourceLike;
  fetchFn: typeof fetch;
  getToken: () => Promise<TokenResult>;
  /** Base URL of the SSE gateway. Defaults to window.PMW.streamGatewayUrl. */
  gatewayBaseUrl?: string;
}

/** Idle period (ms) after the last resolver is removed before the EventSource is closed. */
const IDLE_CLOSE_DELAY_MS = 2 * 60 * 1000;

/** A promise that never resolves — used so only a real JobReady result wins the race. */
const PENDING_FOREVER = new Promise<JobReady>((_resolve) => {
  // Intentionally left pending — this promise is used as a no-op race arm.
});

export class StreamClient {
  #connId: string | null = null;
  #token: string | null = null;
  #ns: string | null = null;
  #connecting: Promise<void> | null = null;
  /** Resolves the first-connect promise exactly once; cleared after first call. */
  #connectResolve: (() => void) | null = null;
  #es: EventSourceLike | null = null;
  #idleTimer: ReturnType<typeof setTimeout> | null = null;
  readonly #resolvers = new Map<string, ResolverEntry>();
  readonly #gatewayBaseUrl: string;

  constructor(private readonly deps: Deps) {
    this.#gatewayBaseUrl = deps.gatewayBaseUrl ?? window.PMW.streamGatewayUrl;
  }

  /**
   * Drops the pending resolver for `key` without resolving it — used when the
   * caller bails (timeout or stop event) so the orphaned resolver is removed and
   * a late SSE event for that key becomes a no-op. Also allows idle-close to
   * fire if the registry is now empty.
   */
  cancelJob(key: string): void {
    if (this.#resolvers.has(key)) {
      this.#onResolverRemoved(key);
    }
  }

  async awaitJob(key: string, immediateCheck: () => Promise<JobReady | null>): Promise<JobReady> {
    this.#cancelIdleTimer();
    await this.#ensureConnected();
    if (!this.#ns) {
      throw new Error('stream-client: namespace is empty after connect');
    }
    const fromEvent = new Promise<JobReady>((resolve) => {
      this.#resolvers.set(key, {resolve, immediateCheck});
    });
    await this.#subscribe(key);
    const fromCheck = immediateCheck().then((r) => r ?? PENDING_FOREVER);
    const result = await Promise.race([fromEvent, fromCheck]);
    // Only clean up here if the resolver is still present — that means the
    // immediate-check path won the race.  If the ready EVENT won, the 'ready'
    // listener already called #onResolverRemoved and scheduled the idle timer;
    // calling it a second time would restart the timer and extend the idle
    // window to ~4 minutes instead of the intended ~2 minutes.
    if (this.#resolvers.has(key)) {
      this.#onResolverRemoved(key);
    }
    return result;
  }

  #onResolverRemoved(key: string): void {
    this.#resolvers.delete(key);
    if (this.#resolvers.size === 0) {
      this.#scheduleIdleClose();
    }
  }

  #scheduleIdleClose(): void {
    this.#cancelIdleTimer();
    this.#idleTimer = setTimeout(() => {
      this.#idleTimer = null;
      if (this.#resolvers.size === 0) {
        this.#closeConnection();
      }
    }, IDLE_CLOSE_DELAY_MS);
  }

  #cancelIdleTimer(): void {
    if (this.#idleTimer !== null) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
  }

  #closeConnection(): void {
    this.#es?.close();
    this.#es = null;
    this.#connId = null;
    this.#connecting = null;
    this.#connectResolve = null;
    this.#token = null;
    this.#ns = null;
  }

  /** POSTs a /subscribe request for `key` using the current connId and token. */
  async #subscribe(key: string): Promise<void> {
    if (!this.#ns) {
      return;
    }
    const channel = `job:${this.#ns}:${key}`;
    await this.deps.fetchFn(`${this.#gatewayBaseUrl}/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.#token}`,
      },
      body: JSON.stringify({connId: this.#connId, channel}),
    });
  }

  /**
   * Re-subscribes every pending key after a reconnect and runs an immediateCheck
   * for each to catch completions that fired during the disconnect window.
   * Fire-and-forget; the fast-path timeout remains the ultimate backstop.
   */
  async #resubscribeAll(): Promise<void> {
    if (!this.#ns) {
      return;
    }
    const entries = Array.from(this.#resolvers.entries());
    await Promise.allSettled(
      entries.map(async ([key, entry]) => {
        await this.#subscribe(key);
        const result = await entry.immediateCheck();
        if (result !== null && this.#resolvers.has(key)) {
          entry.resolve(result);
          this.#onResolverRemoved(key);
        }
      })
    );
  }

  #ensureConnected(): Promise<void> {
    if (this.#connId !== null) {
      return Promise.resolve();
    }
    if (this.#connecting !== null) {
      return this.#connecting;
    }
    const connecting = new Promise<void>((resolve, reject) => {
      this.#connectResolve = resolve;
      void this.deps.getToken().then(({token, ns}) => {
        this.#token = token;
        this.#ns = ns;
        const es = this.deps.eventSourceFactory(
          `${this.#gatewayBaseUrl}/stream?token=${encodeURIComponent(token)}`
        );
        this.#es = es;
        es.addEventListener('connected', (e) => {
          const {connId} = JSON.parse(e.data as string) as ConnectedEvent;
          const isReconnect = this.#connId !== null;
          this.#connId = connId;
          if (this.#connectResolve !== null) {
            this.#connectResolve();
            this.#connectResolve = null;
          }
          if (isReconnect) {
            void this.#resubscribeAll();
          }
        });
        es.addEventListener('ready', (e) => {
          const data = JSON.parse(e.data as string) as JobReady;
          const entry = this.#resolvers.get(data.key);
          if (entry !== undefined) {
            entry.resolve(data);
            this.#onResolverRemoved(data.key);
          }
        });
        es.addEventListener('error', () => {
          reject(new Error('sse-error'));
        });
      }).catch(reject);
    });
    // If the INITIAL connect fails (token fetch throws, or an EventSource 'error'
    // fires before 'connected'), tear down so a later awaitJob retries from scratch
    // instead of reusing this rejected promise for the rest of the page session.
    // After a successful connect the promise is already resolved, so a later
    // mid-stream 'error' is a no-op here and the browser auto-reconnects.
    connecting.catch(() => {
      if (this.#connecting === connecting) {
        this.#closeConnection();
      }
    });
    this.#connecting = connecting;
    return connecting;
  }
}
